import localforage from 'localforage';
import { ICSElement } from '../types/interfaces';

/**
 * Конфигурация LocalForage адаптера
 */
export interface LocalForageConfig {
  name: string;
  storeName: string;
  driver: 'INDEXEDDB' | 'WEBSQL' | 'LOCALSTORAGE';
  description?: string;
  version?: number;
}

/**
 * Адаптер для LocalForage
 */
export class LocalForageAdapter {
  private store: any;
  private isInitialized = false;

  constructor(private config: LocalForageConfig) {
    this.store = localforage.createInstance({
      name: config.name,
      storeName: config.storeName,
      driver: this.getDriverConstant(config.driver),
      description: config.description || `CSElement storage for ${config.name}`,
      version: config.version || 1.0
    });
  }

  private getDriverConstant(driver: string): string {
    switch (driver) {
      case 'INDEXEDDB':
        return localforage.INDEXEDDB;
      case 'WEBSQL':
        return localforage.WEBSQL;
      case 'LOCALSTORAGE':
        return localforage.LOCALSTORAGE;
      default:
        return localforage.INDEXEDDB;
    }
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      await this.store.ready();
      this.isInitialized = true;
    } catch (error) {
      throw new Error(`Не удалось инициализировать LocalForage: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async save(elements: ICSElement[]): Promise<void> {
    await this.initialize();

    const batch = elements.map(element => ({
      key: element.id,
      value: this.serializeElement(element)
    }));

    for (const item of batch) {
      await this.store.setItem(item.key, item.value);
    }
  }

  async load(): Promise<ICSElement[]> {
    await this.initialize();

    const elements: ICSElement[] = [];
    const keys = await this.store.keys();

    for (const key of keys) {
      try {
        const serializedData = await this.store.getItem(key);
        if (serializedData) {
          const element = this.deserializeElement(serializedData);
          elements.push(element);
        }
      } catch (error) {
        console.warn(`Не удалось загрузить элемент ${key}:`, error);
      }
    }

    return elements;
  }

  async loadById(id: string): Promise<ICSElement | null> {
    await this.initialize();

    try {
      const serializedData = await this.store.getItem(id) as string;
      if (!serializedData) return null;

      return this.deserializeElement(serializedData);
    } catch (error) {
      console.warn(`Не удалось загрузить элемент ${id}:`, error);
      return null;
    }
  }

  async delete(id: string): Promise<boolean> {
    await this.initialize();

    try {
      await this.store.removeItem(id);
      return true;
    } catch (error) {
      console.warn(`Не удалось удалить элемент ${id}:`, error);
      return false;
    }
  }

  async clear(): Promise<void> {
    await this.initialize();

    await this.store.clear();
  }

  async count(): Promise<number> {
    await this.initialize();

    const keys = await this.store.keys();
    return keys.length;
  }

  async exists(id: string): Promise<boolean> {
    await this.initialize();

    const keys = await this.store.keys();
    return keys.includes(id);
  }

  async getKeys(): Promise<string[]> {
    await this.initialize();

    return await this.store.keys();
  }

  async getSize(): Promise<number> {
    await this.initialize();

    let totalSize = 0;
    const keys = await this.store.keys();

    for (const key of keys) {
      const value = await this.store.getItem(key) as string;
      if (value) {
        totalSize += new Blob([value]).size;
      }
    }

    return totalSize;
  }

  async iterate<T>(
    callback: (value: string, key: string, iterationNumber: number) => T | void
  ): Promise<T | undefined> {
    await this.initialize();

    return await this.store.iterate(callback);
  }

  async backup(): Promise<{ data: Record<string, any>; metadata: any }> {
    await this.initialize();

    const data: Record<string, any> = {};
    const keys = await this.store.keys();

    for (const key of keys) {
      const value = await this.store.getItem(key);
      data[key] = value;
    }

    return {
      data,
      metadata: {
        timestamp: Date.now(),
        keyCount: keys.length,
        driver: this.config.driver,
        storeName: this.config.storeName
      }
    };
  }

  async restore(backupData: { data: Record<string, any>; metadata: any }): Promise<void> {
    await this.initialize();

    // Очищаем существующие данные
    await this.clear();

    // Восстанавливаем данные
    for (const [key, value] of Object.entries(backupData.data)) {
      await this.store.setItem(key, value);
    }
  }

  async close(): Promise<void> {
    if (this.isInitialized) {
      // LocalForage не требует явного закрытия
      this.isInitialized = false;
    }
  }

  private serializeElement(element: ICSElement): string {
    try {
      // Используем встроенную сериализацию элемента, если доступна
      if (typeof (element as any).serialize === 'function') {
        const serialized = (element as any).serialize({
          includeChildren: true,
          includeData: true,
          includeMetadata: true
        });
        return JSON.stringify({
          ...serialized,
          _timestamp: Date.now(),
          _version: 1
        });
      }

      // Fallback сериализация
      return JSON.stringify({
        id: element.id,
        name: element.name,
        index: element.index,
        data: this.extractElementData(element),
        _timestamp: Date.now(),
        _version: 1
      });
    } catch (error) {
      throw new Error(`Ошибка сериализации элемента ${element.id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  private deserializeElement(serializedData: string): ICSElement {
    try {
      const data = JSON.parse(serializedData);
      
      // Пытаемся использовать встроенную десериализацию, если доступна
      if (typeof (global as any).CSElement?.deserialize === 'function') {
        return (global as any).CSElement.deserialize(data);
      }

      // Fallback десериализация - создаем базовый объект
      return {
        id: data.id,
        name: data.name,
        index: data.index,
        getData: (key: string) => data.data?.[key],
        setData: async (key: string, value: any) => {
          if (!data.data) data.data = {};
          data.data[key] = value;
        },
        deleteData: async (key: string) => {
          if (data.data) {
            delete data.data[key];
            return true;
          }
          return false;
        },
        // Заглушки для других методов
        mainOwner: null,
        data: new Map(Object.entries(data.data || {})),
        getOwner: () => null,
        getOwnerAsync: async () => null,
        getElement: () => null,
        getElementAsync: async () => null,
        getRelativeIndex: () => -1,
        elementsCount: () => 0,
        ownersCount: () => 0,
        getAllElements: () => [],
        getAllOwners: () => [],
        lastElement: null,
        addElement: async () => { throw new Error('Not implemented'); },
        removeElement: async () => false,
        enterLock: async () => {},
        leaveLock: async () => {},
        isLocked: () => false
      } as ICSElement;
    } catch (error) {
      throw new Error(`Ошибка десериализации элемента: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  private extractElementData(element: ICSElement): Record<string, any> {
    const data: Record<string, any> = {};
    
    // Извлекаем основные данные элемента
    if (element.getData) {
      try {
        // Пытаемся получить все данные элемента
        const allData = element.getData('');
        if (typeof allData === 'object' && allData !== null) {
          Object.assign(data, allData);
        }
      } catch {
        // Если не удается получить все данные, игнорируем
      }
    }

    return data;
  }

  /**
   * Получение информации о хранилище
   */
  async getInfo(): Promise<{
    name: string;
    storeName: string;
    driver: string;
    size: number;
    keyCount: number;
  }> {
    await this.initialize();

    const size = await this.getSize();
    const keyCount = await this.count();
    
    return {
      name: this.config.name,
      storeName: this.config.storeName,
      driver: this.config.driver,
      size,
      keyCount
    };
  }

  /**
   * Получение используемого драйвера
   */
  async getDriver(): Promise<string> {
    await this.initialize();

    return this.store.driver();
  }

  /**
   * Проверка поддержки драйвера
   */
  static isDriverSupported(driver: string): boolean {
    const driverConstant = (() => {
      switch (driver) {
        case 'INDEXEDDB':
          return localforage.INDEXEDDB;
        case 'WEBSQL':
          return localforage.WEBSQL;
        case 'LOCALSTORAGE':
          return localforage.LOCALSTORAGE;
        default:
          return localforage.INDEXEDDB;
      }
    })();

    return localforage.supports(driverConstant);
  }

  /**
   * Получение списка поддерживаемых драйверов
   */
  static getSupportedDrivers(): string[] {
    const drivers: string[] = [];
    
    if (localforage.supports(localforage.INDEXEDDB)) {
      drivers.push('INDEXEDDB');
    }
    if (localforage.supports(localforage.WEBSQL)) {
      drivers.push('WEBSQL');
    }
    if (localforage.supports(localforage.LOCALSTORAGE)) {
      drivers.push('LOCALSTORAGE');
    }

    return drivers;
  }
} 